'use client';

/**
 * AstorsReadCard Component
 *
 * Mint-glow narrative card with sparkle icon, uppercase mono overline label
 * ("ASTOR · read" / "ASTOR · summary" / "ASTOR · trend"), narrative body, and
 * an optional row of confidence + tool chips.
 *
 * Three variants drive the default label:
 *   - 'read'    → ASTOR · read    (default)
 *   - 'summary' → ASTOR · summary
 *   - 'trend'   → ASTOR · trend
 *
 * Visual spec source: design bundle (home-astor.jsx Astor's read panel,
 * relations.jsx Borrower slide-over + Borrower Detail Astor's read panel,
 * inbox-watchlist.jsx Watchlist Astor footer). Mint glow + border conventions
 * mirror StageMapDrawer.tsx and BriefViewPopover.tsx.
 */

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useI18n } from '../../hooks';
import { GlassChip } from '../GlassChip';

// ============================================================================
// Types
// ============================================================================

export type AstorsReadVariant = 'read' | 'summary' | 'trend';

export interface AstorsReadCardProps {
  /** Variant drives the label slug. Default 'read'. */
  variant?: AstorsReadVariant;
  /** Override the uppercase mono label. Default derived from variant via i18n. */
  label?: ReactNode;
  /** The narrative body — pre-rendered so callers can embed inline emphasis. */
  narrative: ReactNode;
  /** 0-100. When provided, renders the `conf {percent}%` chip. */
  confidencePercent?: number;
  /** Mono "↳ tool.name" chips. When omitted, the chip row hides. */
  tools?: string[];
  /** Optional action area on the right (Button[s], chip). */
  action?: ReactNode;
  /** Compact density — smaller padding, used in slide-overs / footer notes. Default false. */
  dense?: boolean;
  sx?: SxProps<Theme>;
}

// ============================================================================
// Constants
// ============================================================================

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

// ============================================================================
// Component
// ============================================================================

export function AstorsReadCard({
  variant = 'read',
  label,
  narrative,
  confidencePercent,
  tools,
  action,
  dense = false,
  sx,
}: AstorsReadCardProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';

  const mint = theme.palette.success.main;
  const mintBg = alpha(mint, isDark ? 0.1 : 0.08);
  const mintBorder = alpha(mint, 0.38);

  const resolvedLabel =
    label ??
    (variant === 'summary'
      ? t('astorRead.summaryLabel')
      : variant === 'trend'
        ? t('astorRead.trendLabel')
        : t('astorRead.defaultLabel'));

  const hasTools = Array.isArray(tools) && tools.length > 0;
  const hasConfidence = typeof confidencePercent === 'number';
  const hasAction = action !== undefined && action !== null;
  const showChipRow = hasConfidence || hasTools || hasAction;

  return (
    <Box
      sx={[
        {
          backgroundColor: mintBg,
          border: '1px solid',
          borderColor: mintBorder,
          borderRadius: dense ? '9px' : '11px',
          padding: dense ? 1.25 : 1.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Label row: sparkle + uppercase mono overline */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <AutoAwesomeOutlinedIcon
          aria-hidden
          sx={{
            fontSize: dense ? 14 : 16,
            color: 'success.main',
            flexShrink: 0,
          }}
        />
        <Typography
          variant="overline"
          sx={{
            fontFamily: MONO,
            fontWeight: 600,
            color: 'success.main',
            lineHeight: 1,
          }}
        >
          {resolvedLabel}
        </Typography>
      </Box>

      {/* Narrative body */}
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: 'text.primary',
          lineHeight: 1.55,
          ...(showChipRow && { mb: 1 }),
        }}
      >
        {narrative}
      </Typography>

      {/* Chip row: conf chip + tool chips on left, action on right */}
      {showChipRow && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasConfidence && (
              <GlassChip
                label={t('astorRead.confidence', { percent: confidencePercent })}
                size="small"
                color="success"
                variant="filled"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
            {hasTools &&
              tools.map((tool) => (
                <GlassChip
                  key={tool}
                  label={`${t('astorRead.toolPrefix')} ${tool}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontFamily: MONO,
                  }}
                />
              ))}
          </Box>
          {hasAction && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
      )}
    </Box>
  );
}

AstorsReadCard.displayName = 'AstorsReadCard';

export default AstorsReadCard;
